import { useEffect } from 'react';
import { env } from '../env';
import { getAccessToken } from '../auth/auth0Client';
import type { SseEvent } from './types';

interface Options {
  /** Called once per parsed SSE event (excluding heartbeats / [DONE]) */
  onEvent: (event: SseEvent) => void;
  /** Called on transport-level errors — parent decides whether to show UI */
  onError?: (message: string) => void;
}

/**
 * Shared module-scope connection state. One EventSource for the whole
 * add-in, lifetime ref-counted across hook mounts.
 *
 * Why module-scope and not useRef:
 *   React 18 StrictMode mounts/unmounts/remounts components in dev to surface
 *   bugs. If we kept the EventSource in a useRef, StrictMode's second mount
 *   would open a second EventSource (up to 6 per origin in WKWebView) and
 *   every SSE event would dispatch twice — user sees duplicate AI messages.
 *
 *   A module-level ref + ref count ensures only ONE EventSource is ever open,
 *   regardless of how many times useSSE is mounted.
 */
interface SharedState {
  source: EventSource | null;
  /**
   * In-flight open promise. Set synchronously when openConnection() starts;
   * cleared after the EventSource is constructed (or fails). Prevents the
   * race where multiple concurrent openConnection() calls each pass the
   * `if (shared.source)` guard during the `await getAccessToken()` window
   * (~50ms) and end up creating multiple parallel EventSources. Symptom of
   * that bug: Backend logs show 4 `Register` events in 1 ms per login, all
   * for the same user, each with a different correlation_id. Net effect:
   * every SSE event arrives N times (one per leaked EventSource), all
   * dispatching into the same `shared.listeners` set.
   */
  opening: Promise<void> | null;
  refCount: number;
  lastEventId: string;
  reconnectAttempts: number;
  reconnectTimer: number | null;
  listeners: Set<(event: SseEvent) => void>;
  errorListeners: Set<(msg: string) => void>;
}

const shared: SharedState = {
  source: null,
  opening: null,
  refCount: 0,
  lastEventId: '',
  reconnectAttempts: 0,
  reconnectTimer: null,
  listeners: new Set(),
  errorListeners: new Set(),
};

async function openConnection(): Promise<void> {
  if (shared.source) return; // already open
  if (shared.opening) return shared.opening; // open already in-flight — wait

  // Pin the in-flight promise SYNCHRONOUSLY before the first await so
  // concurrent callers all see it and skip their own EventSource creation.
  const p = (async () => {
    try {
      const token = await getAccessToken();
      // Re-check post-await in case another caller raced past the guard
      // (shouldn't happen because we set `shared.opening` synchronously,
      // but defensive against future re-entries).
      if (shared.source) return;
      const url = new URL(`${env.apiUrl}/api/v1/notification/sse`);
      url.searchParams.set('token', token);
      if (shared.lastEventId) url.searchParams.set('last_event_id', shared.lastEventId);

      const es = new EventSource(url.toString(), { withCredentials: true });
      shared.source = es;

    es.onopen = () => {
      shared.reconnectAttempts = 0;
    };

    es.onmessage = (e) => {
      if (!e.data || e.data === '[DONE]') return;
      if (typeof e.data === 'string' && e.data.includes('heartbeat')) return;
      if (e.lastEventId) shared.lastEventId = e.lastEventId;
      let parsed: SseEvent;
      try {
        parsed = JSON.parse(e.data) as SseEvent;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[SSE] Failed to parse event', err, e.data);
        return;
      }
      for (const listener of shared.listeners) {
        try {
          listener(parsed);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[SSE] Listener threw', err);
        }
      }
    };

      es.onerror = () => {
        es.close();
        shared.source = null;
        scheduleReconnect();
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'SSE connection failed';
      for (const l of shared.errorListeners) l(message);
      scheduleReconnect();
    } finally {
      // Always clear the in-flight gate so the next legitimate call can
      // open a fresh connection (e.g. after onerror tears down source).
      shared.opening = null;
    }
  })();
  shared.opening = p;
  return p;
}

function scheduleReconnect(): void {
  if (shared.refCount === 0) return; // no listeners, no need to reconnect
  if (shared.reconnectTimer !== null) return; // already scheduled
  shared.reconnectAttempts += 1;
  const delay = Math.min(30_000, 500 * 2 ** Math.min(shared.reconnectAttempts, 6));
  shared.reconnectTimer = window.setTimeout(() => {
    shared.reconnectTimer = null;
    openConnection();
  }, delay);
}

function closeIfIdle(): void {
  if (shared.refCount > 0) return;
  if (shared.reconnectTimer !== null) {
    window.clearTimeout(shared.reconnectTimer);
    shared.reconnectTimer = null;
  }
  if (shared.source) {
    shared.source.close();
    shared.source = null;
  }
}

/**
 * Pre-warm the SSE connection without registering a listener.
 *
 * Why: useSSE only calls openConnection() when a component subscribes —
 * which happens inside TaskPane → useChat. That delays the SSE handshake
 * by 200-500ms past authentication. If the user sends their first message
 * before the handshake completes, the AI's reply may publish to SNS
 * before the SSE channel is up, and the live update is missed.
 *
 * Mount this in AuthedTaskPane (above TaskPane in the tree) right after
 * auth completes. The connection opens immediately and is reused by the
 * useSSE call inside useChat — no double-open, no duplicate events
 * (the module-scope `shared.source` guard in openConnection prevents both).
 *
 * SOURCE: frontend-prod/app/providers/SSEProvider.tsx (web's equivalent
 * mounts at app root via a Redux thunk; plugin uses this lighter hook).
 */
export function useSSEPrewarm(): void {
  useEffect(() => {
    if (!shared.source) {
      // Fire-and-forget. Errors handled inside openConnection.
      openConnection();
    }
    // No teardown — we want the connection to outlive any individual
    // listener. Closing happens only when refCount hits 0 in useSSE,
    // which won't happen until the whole app unmounts.
  }, []);
}

/**
 * Subscribe to the shared SSE connection. Ref-counts lifetime so the
 * EventSource is only opened once and only closed when no components
 * are listening.
 */
export function useSSE({ onEvent, onError }: Options): void {
  useEffect(() => {
    shared.listeners.add(onEvent);
    if (onError) shared.errorListeners.add(onError);
    shared.refCount += 1;

    // Open the connection lazily — subsequent mounts reuse it.
    if (!shared.source) {
      openConnection();
    }

    return () => {
      shared.listeners.delete(onEvent);
      if (onError) shared.errorListeners.delete(onError);
      shared.refCount -= 1;
      // Debounce close: StrictMode's re-mount happens synchronously after
      // unmount, so if we closed immediately we'd flap. A short deferral
      // lets the remount re-register before we tear anything down.
      window.setTimeout(closeIfIdle, 100);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
